import React, { useState } from 'react';
import { IconPlus, IconMinus, IconShield } from './icons';
import { useLanguage } from '../contexts/LanguageContext';
import { LOCATION_ADDRESS } from '../config';
import { DEALERSHIP } from '../data/dealership';
import { Reveal } from './Reveal';
import { Tilt3D } from './Tilt3D';
export function Faq() {
  const {
    t,
    lang
  } = useLanguage();
  const [open, setOpen] = useState<string | null>(null);

  // Every answer stays conservative — no promise of approval, rates, terms, inventory, or figures.
  const items = [{
    id: 'q1',
    question: t('faq.q1'),
    answer: t('faq.generic')
  }, {
    id: 'q2',
    question: t('faq.q2'),
    answer: t('faq.generic')
  }, {
    id: 'q3',
    question: t('faq.q3'),
    answer: t('faq.generic')
  }, {
    id: 'q4',
    question: t('faq.q4'),
    answer: t('faq.generic')
  },
  // 'q5' (can I trade in my current vehicle) was merged into 'q9' — same question.
  {
    id: 'q9',
    question: t('faq.q9'),
    answer: t('faq.a9')
  }, {
    id: 'q10',
    question: t('faq.q10'),
    answer: t('faq.a10')
  }, {
    id: 'q11',
    question: t('faq.q11'),
    answer: t('faq.a11')
  }, {
    id: 'q6',
    question: t('faq.q6'),
    answer: t('faq.a6')
  }, {
    id: 'q7',
    question: t('faq.q7'),
    answer: t('faq.a7')
  }, {
    id: 'q8',
    question: t('faq.q8'),
    // Uses the client-approved dealership address; config.LOCATION_ADDRESS overrides it if set.
    answer: `${t('faq.a8.base')} ${LOCATION_ADDRESS || `${DEALERSHIP.name} · ${DEALERSHIP.address}`}`
  }];
  return <section className="w-full bg-offwhite" data-id="element-62" data-mp="bi6hv3rdzt8i">
      <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 md:py-24" data-id="element-63" data-mp="biye8gst5uzu">
        <Reveal data-id="element-64" data-mp="3szfwaqvcjecw">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-[0.16em] text-ink sm:text-3xl" data-id="element-65" data-mp="3szzfsc0naat1">
            {t('faq.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-66" data-mp="3jxymtrcg2ax6" />
        </Reveal>

        <dl className="mt-8 border-t border-camel/40" data-id="element-67" data-mp="3nzk4dpzxbddp">
          {items.map((item, index) => {
          const isOpen = open === item.id;
          return <Reveal key={item.id} delay={Math.min(index * 0.03, 0.2)} data-id="element-68" data-mp="1uifgfjqy1qnd" data-mpi={index}>
                <div className="border-b border-camel/40" data-id="element-69" data-mp="1pp368en0hme5">
                  <dt data-id="element-70" data-mp="1kr1buxk4y0nd">
                    <button type="button" onClick={() => setOpen(isOpen ? null : item.id)} aria-expanded={isOpen} aria-controls={`faq-panel-${item.id}`} id={`faq-button-${item.id}`} className="flex min-h-[56px] w-full items-center justify-between gap-4 py-4 text-left" data-id="element-71" data-mp="1kqi4ubw0wwo4">
                      <span className={`text-base font-semibold ${isOpen ? 'text-kred' : 'text-ink'}`} data-id="element-72" data-mp="23g7wwr8ej4cc">
                        {item.question}
                      </span>
                      <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${isOpen ? 'border-kred text-kred' : 'border-camel/50 text-muted'}`} data-id="element-73" data-mp="s2vrwd0wofol">
                        {isOpen ? <IconMinus className="h-4 w-4" data-id="element-74" data-mp="38oso1h8nn1gk" /> : <IconPlus className="h-4 w-4" data-id="element-75" data-mp="38mzdvryu1z3t" />}
                      </span>
                    </button>
                  </dt>
                  <dd id={`faq-panel-${item.id}`} role="region" aria-labelledby={`faq-button-${item.id}`} hidden={!isOpen} className="pb-5 pr-8 text-sm leading-relaxed text-body" data-id="element-76" data-mp="3idu4t9n0xmvl">
                    {item.answer}
                  </dd>
                </div>
              </Reveal>;
        })}
        </dl>

        {/* Verified manufacturer information — used exactly as provided, with its limitation note. */}
        <Reveal delay={0.1} className="mt-10" data-id="element-77" data-mp="2226tguofz5ud">
          <Tilt3D max={3} lift={8} data-id="element-78" data-mp="16p5nsp8ex8ub">
            <aside className="border-l-2 border-camel bg-white p-6 shadow-[0_18px_40px_-34px_rgba(5,20,31,0.6)]" data-id="element-79" data-mp="16sa24ffldkyp">
            <h3 className="flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-[0.2em] text-camelink" data-id="element-80" data-mp="1god3hj2mdfkl">
              <IconShield className="h-5 w-5" data-id="element-81" data-mp="1bp0h0zfnuaij" />
              {t('warranty.title')}
            </h3>
            <p className="mt-3 text-base leading-relaxed text-body" data-id="element-82" data-mp="nywfqypy2mup">{t('warranty.text')}</p>
            <p className="mt-3 text-xs leading-relaxed text-muted" data-id="element-83" data-mp="j14lhy1tuonc">{t('warranty.small')}</p>
              <p className="mt-4 text-xs text-muted" data-id="element-84" data-mp="xq0srsw27xmw">
                {DEALERSHIP.name} · {DEALERSHIP.address} · {DEALERSHIP.hours[lang]}
              </p>
            </aside>
          </Tilt3D>
        </Reveal>
      </div>
    </section>;
}